import React from "react";
import { Bar, Radar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  RadialLinearScale,
  Filler,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, RadialLinearScale, BarElement, LineElement, PointElement, Filler, Title, Tooltip, Legend);

const BreakdownBarChart = ({
  title,
  labels = [],
  datasets = [],
  showPercentAxis = false,
  countAxis = false,
  radar = false,
  headerMeta = null,
  tooltipLabelFormatter = null,
}) => {
  const isMobile = typeof window !== "undefined" && window.innerWidth <= 768;
  const hasData = labels.length > 0 && datasets.some((dataset) => dataset.data?.length);

  const data = {
    labels,
    datasets: datasets.map((dataset) => ({
      borderRadius: dataset.type === "line" ? 0 : 10,
      borderSkipped: dataset.type === "line" ? undefined : false,
      maxBarThickness: dataset.type === "line" ? undefined : 34,
      tension: dataset.type === "line" ? 0.35 : undefined,
      pointRadius: dataset.type === "line" ? 3 : undefined,
      pointHoverRadius: dataset.type === "line" ? 5 : undefined,
      ...dataset,
    })),
  };

  const options = radar ? {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: datasets.length > 1,
        labels: {
          color: "#f8f9fa",
        },
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            if (tooltipLabelFormatter) {
              return tooltipLabelFormatter(context);
            }

            return `${context.dataset.label}: ${context.parsed.r}`;
          },
        },
      },
    },
    scales: {
      r: {
        angleLines: {
          color: "rgba(248, 249, 250, 0.12)",
        },
        grid: {
          color: "rgba(248, 249, 250, 0.12)",
        },
        pointLabels: {
          color: "#f8f9fa",
          font: {
            size: isMobile ? 10 : 11,
          },
        },
        ticks: {
          display: false,
          showLabelBackdrop: false,
        },
      },
    },
  } : {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: datasets.length > 1,
        labels: {
          color: "#f8f9fa",
        },
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            if (tooltipLabelFormatter) {
              return tooltipLabelFormatter(context);
            }

            if (context.dataset.yAxisID === "yPercent") {
              return `${context.dataset.label}: ${context.parsed.y}%`;
            }

            if (countAxis) {
              return `${context.dataset.label}: ${context.parsed.y}`;
            }

            return `${context.dataset.label}: £${context.parsed.y}`;
          },
        },
      },
    },
    scales: {
      x: {
        ticks: {
          color: "#f8f9fa",
          font: {
            size: isMobile ? 10 : 12,
          },
          maxRotation: 45,
          minRotation: 45,
        },
        grid: {
          display: false,
        },
      },
      y: {
        beginAtZero: true,
        ticks: {
          color: "#f8f9fa",
          font: {
            size: isMobile ? 10 : 12,
          },
          precision: countAxis ? 0 : undefined,
          callback: (value) => (countAxis ? value : `£${value}`),
        },
        grid: {
          color: "rgba(248, 249, 250, 0.1)",
        },
      },
      ...(showPercentAxis ? {
        yPercent: {
          position: "right",
          min: 0,
          max: 100,
          ticks: {
            color: "#f6c177",
            font: {
              size: isMobile ? 10 : 12,
            },
            callback: (value) => `${value}%`,
          },
          grid: {
            drawOnChartArea: false,
          },
        },
      } : {}),
    },
  };

  return (
    <div className="dashboard-breakdown-panel app-widget-panel">
      <div className="dashboard-breakdown-panel__header">
        <h3 className="dashboard-breakdown-panel__title app-widget-title">{title}</h3>
        {headerMeta ? <div className="dashboard-breakdown-panel__meta">{headerMeta}</div> : null}
      </div>
      <div className="dashboard-breakdown-panel__chart-shell">
        {hasData ? (
          <div className="dashboard-breakdown-panel__chart">
            {radar ? <Radar data={data} options={options} /> : <Bar data={data} options={options} />}
          </div>
        ) : (
          <div className="dashboard-breakdown-panel__empty">No trade data yet</div>
        )}
      </div>
    </div>
  );
};

export default BreakdownBarChart;
